import React, { useEffect, useState } from "react";
import axiosClient from "../../axios";
import SideBar from "../SideBar";
// import InvoiceSummaryCards from "./InvoiceSummaryCards";
import CashFlowCard from "./CashFlowCard";
import RevenuePerformanceCard from "./RevenuePerformanceCard";
import OverdueCard from "./OverdueCard";
import FinanceOperationsOutstanding from "./FinanceOperationsOutstanding";
import PartnersRevenueTable from "./PartnersRevenueTable";
import EarningsDistributionChart from "./EarningsDistributionChart";
import RevenuePeriodSelector from "./RevenuePeriodSelector";
import { formatMonthRangeLabel, validateMonthRange } from "./revenueSummaryUtils";
import "../../assets/css/AdminCommon.css";
import "../../assets/css/revenueSummary.css";

const normalizeRangeResponse = (payload) => {
	const { range, combined, months } = payload;
	return {
		...combined,
		month: range.endMonth,
		periodLabel: formatMonthRangeLabel(range.startMonth, range.endMonth),
		isRange: true,
		rangeMeta: range,
		monthsBreakdown: months,
		fromCache: false,
	};
};

const RevenueAnalytics = () => {
	const [viewMode, setViewMode] = useState("single");
	const [selectedMonth, setSelectedMonth] = useState("");
	const [startMonth, setStartMonth] = useState("");
	const [endMonth, setEndMonth] = useState("");
	const [revenueData, setRevenueData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [hardRefreshing, setHardRefreshing] = useState(false);

	const fetchSingleMonth = (month, hardRefresh = false) => {
		if (hardRefresh) {
			setHardRefreshing(true);
		} else {
			setLoading(true);
		}

		const url = hardRefresh
			? `/admin/revenue-summary?month=${month}&hardRefresh=true`
			: `/admin/revenue-summary?month=${month}`;

		return axiosClient
			.get(url)
			.then((res) => {
				setRevenueData(res?.data?.response || null);
			})
			.catch((err) => {
				console.log("error fetching revenue analytics", err);
				alert(err?.response?.data?.message?.message || err?.response?.data?.message || "Error fetching revenue data");
			})
			.finally(() => {
				setLoading(false);
				setHardRefreshing(false);
			});
	};

	const fetchMonthRange = (start, end) => {
		setLoading(true);

		return axiosClient
			.post("/admin/revenue-summary-range", {
				start_month: start,
				end_month: end,
			})
			.then((res) => {
				const payload = res?.data?.response;
				setRevenueData(payload?.combined ? normalizeRangeResponse(payload) : null);
			})
			.catch((err) => {
				console.log("error fetching revenue range", err);
				const msg = err?.response?.data?.message;
				alert(typeof msg === "string" ? msg : msg?.message || "Error fetching combined revenue data");
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const rangeValidation = validateMonthRange(startMonth, endMonth);
	const hasValidSelection =
		viewMode === "single"
			? Boolean(selectedMonth)
			: rangeValidation.valid;

	useEffect(() => {
		if (viewMode === "single") {
			if (!selectedMonth) {
				setRevenueData(null);
				return;
			}
			fetchSingleMonth(selectedMonth);
			return;
		}

		if (!rangeValidation.valid) {
			setRevenueData(null);
			return;
		}

		fetchMonthRange(startMonth, endMonth);
	}, [viewMode, selectedMonth, startMonth, endMonth]);

	const handleHardRefresh = () => {
		if (viewMode !== "single") return;
		if (!selectedMonth) {
			alert("Please select a month first");
			return;
		}
		fetchSingleMonth(selectedMonth, true);
	};

	const refreshDisabled = hardRefreshing || viewMode !== "single" || !selectedMonth;
	const referenceMonth = revenueData?.month || (viewMode === "single" ? selectedMonth : endMonth);
	const periodSubtitle = revenueData?.isRange && revenueData?.rangeMeta
		? `Combined: ${revenueData.periodLabel} (${revenueData.rangeMeta.monthCount} months)`
		: null;

	return (
		<>
			<SideBar handleSideBar={true} activeTab={"Revenue"} />
			<div className="admin-screen-v4 rs-page">
				<div className="admin-form-container-v4 rs-section">
					<div className="admin-header-v4">
						<div>
							<h2>Revenue Analytics</h2>
							<p className="rs-subtitle">
								Billing-type-aware revenue — INR and USD shown separately
								{periodSubtitle && (
									<span className="rs-range-badge">{periodSubtitle}</span>
								)}
								{revenueData?.fromCache && (
									<span className="rs-cache-badge">CACHED</span>
								)}
							</p>
						</div>
						<div className="rs-header-actions">
							<button
								onClick={handleHardRefresh}
								disabled={refreshDisabled}
								className="rs-hard-refresh-btn"
								title={viewMode === "range" ? "Hard refresh is only available for single month view" : undefined}
							>
								{hardRefreshing ? "Refreshing..." : "Hard Refresh"}
							</button>
							<RevenuePeriodSelector
								viewMode={viewMode}
								setViewMode={setViewMode}
								selectedMonth={selectedMonth}
								setSelectedMonth={setSelectedMonth}
								startMonth={startMonth}
								setStartMonth={setStartMonth}
								endMonth={endMonth}
								setEndMonth={setEndMonth}
							/>
						</div>
					</div>
				</div>

				{!hasValidSelection ? (
					<div className="empty-state-v4">
						<div className="empty-icon-v4">📅</div>
						<h3>{viewMode === "single" ? "Select a Month" : "Select a Month Range"}</h3>
						<p>
							{viewMode === "single"
								? "Choose a month above to load revenue analytics"
								: "Choose start and end month (up to 12 months) for combined totals"}
						</p>
					</div>
				) : loading ? (
					<div className="loader-container-v4">
						<div className="loader-image-v4">📊</div>
						<p className="rs-loader-text">
							{viewMode === "range" ? "Loading combined analytics..." : "Loading analytics..."}
						</p>
					</div>
				) : revenueData ? (
					<>
						<div className="admin-form-container-v4 rs-section">
							<CashFlowCard cashFlow={revenueData.cashFlow} />
							<RevenuePerformanceCard
								revenue={revenueData.revenue}
								month={revenueData.month}
								periodLabel={revenueData.periodLabel}
							/>
							<OverdueCard overdue={revenueData.overdue} />
						</div>

						{/* Summary section — hidden for now
						<div className="admin-form-container-v4 rs-section">
							<InvoiceSummaryCards summary={revenueData.summary} />
						</div>
						*/}

						<div className="admin-form-container-v4 rs-section">
							<PartnersRevenueTable
								partners={revenueData.partners}
								internalEarnings={revenueData.internalEarnings}
								selectedMonth={referenceMonth}
							/>
						</div>

						<div className="admin-form-container-v4 rs-section">
							<EarningsDistributionChart data={revenueData.charts?.earningsDistribution} />
						</div>

						<FinanceOperationsOutstanding summary={revenueData.summary} />
					</>
				) : (
					<div className="empty-state-v4">
						<div className="empty-icon-v4">📭</div>
						<h3>No Data Available</h3>
						<p>No revenue data found for the selected period</p>
					</div>
				)}
			</div>
		</>
	);
};

export default RevenueAnalytics;
